import { updateStreak } from "../utils/streakUtils";
import { useEffect, useState } from "react";
import { auth, db, provider } from "../firebase";
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  getRedirectResult,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { doc, setDoc, getDoc, getDocs, collection, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { examTypes } from "../utils/config";
import { motion, AnimatePresence } from "framer-motion";
import { containsBadWord } from "../utils/badWords";
import { toast } from "sonner";
import { FiX } from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";
import "./AuthPage.css";
import "./AuthPage.css";

function AuthPage({ onClose }) {
  const navigate = useNavigate();
  const [emailOrUsername, setEmailOrUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState("");
  const [selectedExam, setSelectedExam] = useState("TYT–AYT"); // Varsayılan değer güncel listeye uygun
  const [referralCode, setReferralCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    const checkRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          const userId = result.user.uid;
          const userRef = doc(db, "users", userId);
          const userSnap = await getDoc(userRef);

          if (!userSnap.exists()) {
            navigate("/google-setup", {
              state: { userId, displayName: result.user.displayName, photoURL: result.user.photoURL },
            });
          } else {
            const data = userSnap.data();
            localStorage.setItem("username", data.username);
            localStorage.setItem("examType", data.examType);
            localStorage.setItem("userId", userId);
            localStorage.setItem("freshLogin", "true");
            await updateStreak(userId, "login");
            toast.success("Giriş başarılı!");
            setTimeout(() => {
              onClose();
              navigate("/home", { replace: true });
            }, 300);
          }
        }
      } catch (err) {
        setError(`Google Girişi Başarısız: ${getFriendlyErrorMessage(err)}. Lütfen tekrar deneyin.`);
        toast.error(`Google Girişi Başarısız: ${getFriendlyErrorMessage(err)}`);
      } finally {
        setLoading(false);
      }
    };

    checkRedirectResult();
  }, [navigate, onClose]);

  const saveUserToFirestore = async (userId, name, exam, email, referralCodeUsed) => {
    if (containsBadWord(name)) {
      toast.error("Kullanıcı adı uygun olmayan kelimeler içeriyor. Lütfen başka bir kullanıcı adı seçin.");
      throw new Error("Kullanıcı adı uygun olmayan kelimeler içeriyor.");
    }

    if (referralCodeUsed && containsBadWord(referralCodeUsed)) {
      toast.error("Referans kodu uygun olmayan kelimeler içeriyor. Lütfen başka bir kod seçin.");
      throw new Error("Referans kodu uygun olmayan kelimeler içeriyor.");
    }

    try {
      const generatedReferralCode = `${name}-${Math.random().toString(36).substr(2, 8)}`;
      const userRef = doc(db, "users", userId);
      await setDoc(userRef, {
        username: name,
        examType: exam,
        email: email,
        emailVerified: false,
        referralCode: generatedReferralCode,
        referralCodeUsed: referralCodeUsed || null,
        isPremium: false,
        createdAt: new Date().toISOString(),
      });
      localStorage.setItem("username", name);
      localStorage.setItem("examType", exam);
    } catch (err) {
      setError(`Kullanıcı bilgileri kaydedilirken hata oluştu: ${getFriendlyErrorMessage(err)}. Lütfen tekrar deneyin.`);
      toast.error(`Kullanıcı bilgileri kaydedilirken hata oluştu: ${getFriendlyErrorMessage(err)}`);
      throw err;
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError("");

    try {
      await setPersistence(auth, browserLocalPersistence);
      const result = await signInWithPopup(auth, provider);
      await processGoogleLogin(result);
    } catch (err) {
      console.error("Google Login Error:", err);
      if (err.code === "auth/popup-blocked") {
        setError(
          "Pop-up engellendi. Lütfen tarayıcınızın pop-up ayarlarını kontrol edin ve tekrar deneyin. Alternatif olarak, e-posta ile giriş yapmayı deneyebilirsiniz."
        );
        toast.error(
          "Pop-up engellendi. Lütfen tarayıcınızın pop-up ayarlarını kontrol edin veya e-posta ile giriş yapmayı deneyin."
        );
      } else {
        setError(`Google Girişi Başarısız: ${getFriendlyErrorMessage(err)}. Lütfen tekrar deneyin.`);
        toast.error(`Google Girişi Başarısız: ${getFriendlyErrorMessage(err)}`);
      }
      setLoading(false);
    }
  };

  const processGoogleLogin = async (result) => {
    const userId = result.user.uid;
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      navigate("/google-setup", {
        state: { userId, displayName: result.user.displayName, photoURL: result.user.photoURL },
      });
    } else {
      const data = userSnap.data();
      localStorage.setItem("username", data.username);
      localStorage.setItem("examType", data.examType);
      localStorage.setItem("userId", userId);
      localStorage.setItem("freshLogin", "true");
      await updateStreak(userId, "login");
      toast.success("Giriş başarılı!");
      setTimeout(() => {
        onClose();
        navigate("/home", { replace: true });
      }, 300);
    }
  };

  const findUserByUsername = async (username) => {
    const q = query(collection(db, "users"), where("username", "==", username));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      return { id: userDoc.id, ...userDoc.data() };
    }
    return null;
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (isLogin) {
        const rawInput = emailOrUsername.trim();
        let emailToUse = rawInput;
        
        if (!rawInput) {
          setError("Lütfen e-posta veya kullanıcı adı girin.");
          setLoading(false);
          return;
        }
        
        if (!password) {
          setError("Lütfen şifrenizi girin.");
          setLoading(false);
          return;
        }

        if (!rawInput.includes("@")) {
          const userData = await findUserByUsername(rawInput);
          if (!userData) {
            setError("Kullanıcı adı bulunamadı. Lütfen doğru bir kullanıcı adı veya e-posta girin.");
            toast.error("Kullanıcı adı bulunamadı.");
            setLoading(false);
            return;
          }
          if (!userData.email) {
            setError("Bu kullanıcı hesabına bağlı bir e-posta adresi bulunamadı.");
            toast.error("Geçersiz kullanıcı profili.");
            setLoading(false);
            return;
          }
          emailToUse = userData.email;
        }

        const result = await signInWithEmailAndPassword(auth, emailToUse, password);
        const userId = result.user.uid;
        const userSnap = await getDoc(doc(db, "users", userId));
        if (userSnap.exists()) {
          const data = userSnap.data();
          localStorage.setItem("username", data.username);
          localStorage.setItem("examType", data.examType);
        }
        localStorage.setItem("userId", userId);
        localStorage.setItem("freshLogin", "true");
        await updateStreak(userId, "login");
        toast.success("Giriş başarılı!");
        setTimeout(() => {
          onClose();
          navigate("/home", { replace: true });
        }, 300);
      } else {
        if (!privacyAccepted) {
          setError("Lütfen gizlilik politikasını kabul edin.");
          toast.error("Lütfen gizlilik politikasını kabul edin.");
          setLoading(false);
          return;
        }
        if (containsBadWord(username)) {
          setError("Kullanıcı adı uygun olmayan kelimeler içeriyor. Lütfen başka bir kullanıcı adı seçin.");
          toast.error("Kullanıcı adı uygun olmayan kelimeler içeriyor. Lütfen başka bir kullanıcı adı seçin.");
          setLoading(false);
          return;
        }
        if (referralCode && containsBadWord(referralCode)) {
          setError("Referans kodu uygun olmayan kelimeler içeriyor. Lütfen başka bir kod seçin.");
          toast.error("Referans kodu uygun olmayan kelimeler içeriyor.");
          setLoading(false);
          return;
        }
        const userExists = await findUserByUsername(username.trim());
        if (userExists) {
          setError("Bu kullanıcı adı zaten kullanılıyor. Lütfen başka bir kullanıcı adı seçin.");
          toast.error("Bu kullanıcı adı zaten kullanılıyor.");
          setLoading(false);
          return;
        }
        
        const cleanEmail = emailOrUsername.trim();
        if (!cleanEmail.includes("@")) {
           setError("Kayıt olmak için lütfen geçerli bir e-posta adresi girin.");
           toast.error("Geçerli bir e-posta gerekli.");
           setLoading(false);
           return;
        }

        const result = await createUserWithEmailAndPassword(auth, cleanEmail, password);
        const userId = result.user.uid;
        await saveUserToFirestore(userId, username.trim(), selectedExam, cleanEmail, referralCode ? referralCode.trim() : "");
        localStorage.setItem("userId", userId);
        localStorage.setItem("freshLogin", "true");
        toast.success("Kayıt başarılı! Giriş yapabilirsiniz.");
        setTimeout(() => {
          onClose();
          navigate("/home", { replace: true });
        }, 300);
      }
    } catch (err) {
      setError(`Hata: ${getFriendlyErrorMessage(err)}. Lütfen bilgilerinizi kontrol edin ve tekrar deneyin.`);
      toast.error(`Hata: ${getFriendlyErrorMessage(err)}`);
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetError("");

    if (!resetEmail.includes("@")) {
      setResetError("Lütfen geçerli bir e-posta adresi girin.");
      toast.error("Lütfen geçerli bir e-posta adresi girin.");
      setResetLoading(false);
      return;
    }

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      toast.success("Şifre sıfırlama bağlantısı e-postanıza gönderildi!");
      setResetEmail("");
      setShowResetModal(false);
    } catch (err) {
      setResetError(`Hata: ${getFriendlyErrorMessage(err)}`);
      toast.error(`Hata: ${getFriendlyErrorMessage(err)}`);
    } finally {
      setResetLoading(false);
    }
  };

  const getFriendlyErrorMessage = (error) => {
    switch (error.code) {
      case "auth/popup-closed-by-user":
        return "Google giriş penceresini kapattınız. Lütfen tekrar deneyin.";
      case "auth/popup-blocked":
        return "Açılır pencere engellendi. Lütfen tarayıcınızın açılır pencere ayarlarını kontrol edin ve tekrar deneyin.";
      case "auth/account-exists-with-different-credential":
        return "Bu e-posta adresi başka bir giriş yöntemiyle zaten kayıtlı. Lütfen farklı bir yöntemle giriş yapmayı deneyin.";
      case "auth/invalid-email":
        return "Geçersiz e-posta adresi. Lütfen doğru bir e-posta adresi girin.";
      case "auth/user-not-found":
      case "auth/wrong-password":
      case "auth/invalid-credential":
      case "auth/invalid-login-credentials":
        return "E-posta adresiniz veya şifreniz hatalı. Lütfen bilgilerinizi kontrol edip tekrar deneyin.";
      case "auth/email-already-in-use":
        return "Bu e-posta adresi zaten kullanılıyor. Lütfen farklı bir e-posta adresi kullanın veya giriş yapmayı deneyin.";
      case "auth/network-request-failed":
        return "Ağ bağlantı hatası. Lütfen internet bağlantınızı kontrol edin ve tekrar deneyin.";
      default:
        return "Bir hata oluştu. Lütfen bilgilerinizi kontrol edip tekrar deneyin.";
    }
  };

  return (
    <div className="auth-container">
      <AnimatePresence>
        {error && (
          <motion.div
            key="error-msg"
            className="auth-error"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {loading && (
          <motion.div
            key="loading-overlay"
            className="loading-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="spinner"></div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        type="button"
        onClick={handleGoogleLogin}
        className="google-button"
        disabled={loading}
      >
        <FcGoogle size={20} />
        Google ile devam et
      </button>

      <div className="auth-divider">veya e-posta ile</div>

      <form onSubmit={handleEmailAuth} className="auth-form">
        <AnimatePresence>
          {!isLogin && (
            <motion.div
              key="signup-fields"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              style={{ display: "flex", flexDirection: "column", gap: "15px", overflow: "hidden" }}
            >
              <div className="auth-input-group">
                <input
                  type="text"
                  placeholder="Kullanıcı Adı"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="auth-input"
                />
                <span className="auth-warning">⚠️ İlerde değiştirilemez</span>
              </div>
              
              <div className="auth-input-group">
                <select
                  value={selectedExam}
                  onChange={(e) => setSelectedExam(e.target.value)}
                  className="auth-input"
                >
                  {examTypes.map((exam, i) => (
                    <option key={i} value={exam}>
                      {exam}
                    </option>
                  ))}
                </select>
              </div>

              <div className="auth-input-group">
                <input
                  type="text"
                  placeholder="Referans Kodu (Opsiyonel)"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value)}
                  className="auth-input"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="auth-input-group">
          <input
            type="text"
            placeholder={isLogin ? "E-posta veya Kullanıcı Adı" : "E-posta"}
            value={emailOrUsername}
            onChange={(e) => setEmailOrUsername(e.target.value)}
            required
            className="auth-input"
          />
        </div>

        <div className="auth-input-group">
          <input
            type="password"
            placeholder="Şifre"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="auth-input"
            autoComplete="current-password"
          />
        </div>

        {isLogin && (
          <button
            type="button"
            className="auth-forgot-password"
            onClick={() => setShowResetModal(true)}
          >
            Şifreni mi unuttun?
          </button>
        )}

        <AnimatePresence>
          {!isLogin && (
            <motion.div
              key="privacy-container"
              className="auth-privacy"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
            >
              <input
                type="checkbox"
                id="privacy"
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
              />
              <label htmlFor="privacy">
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Gizlilik Politikasını
                </a>{" "}
                kabul ediyorum.
              </label>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          type="submit"
          className="auth-submit-btn"
          disabled={loading || (!isLogin && !privacyAccepted)}
        >
          {isLogin ? "Giriş Yap" : "Kayıt Ol"}
        </button>
      </form>


      <div className="auth-toggle-mode" onClick={() => setIsLogin(!isLogin)}>
        {isLogin ? (
          <>Hesabın yok mu? <span>Kayıt ol</span></>
        ) : (
          <>Zaten hesabın var mı? <span>Giriş yap</span></>
        )}
      </div>

      <AnimatePresence>
        {showResetModal && (
          <motion.div
            key="reset-modal"
            className="reset-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="reset-modal-content"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--text-primary)' }}>Şifreni Sıfırla</h3>
                <button onClick={() => setShowResetModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  <FiX size={20} />
                </button>
              </div>
              
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '24px' }}>
                E-posta adresinize bir şifre sıfırlama bağlantısı göndereceğiz.
              </p>

              <form onSubmit={handlePasswordReset} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {resetError && (
                  <div className="auth-error">{resetError}</div>
                )}
                
                <input
                  type="email"
                  placeholder="E-posta Adresi"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  className="auth-input"
                />

                <button
                  type="submit"
                  className="auth-submit-btn"
                  disabled={resetLoading}
                >
                  {resetLoading ? "Gönderiliyor..." : "Bağlantı Gönder"}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default AuthPage;